import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import {PropertyType, ResourceType} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {bookmarkOverrides, BookmarkOverrides} from 'common/report/util/bookmark-overrides';
import {rison} from 'common/util/rison';
import {CohortOperatorListMap, ListOperator} from 'common/widgets/property-filter-menu/models/types';
import {relativeDatesToFullLengthDateRange} from 'common/util/date-converters';
import {ChartType, FunnelBookmark, TrendCountType, TrendUnit} from 'common/report/models/funnels/types';
import {fromEnum} from 'common/report/util/funnels/utils';

import {paramsFromGroupBySelectedCohorts, paramsFromSegfilter} from './param-converters';

function passThrough(param, ...path) {
  return (source) => {
    const obj = path.reduce((node, attr) => node[attr], source);
    const value = obj[param];
    if (!isNil(value) && (!Array.isArray(value) || value.length > 0)) {
      return {[param]: value};
    }
    return null;
  };
}

export const URL_APP_PARAMS = [
  `eventIndex`,
  `startStep`,
  `selected`,
  `selectMode`,
  `trendType`,
  `frequencyType`,
  `frequencyVizType`,
  `querySampling`,
  `ttcVizType`,
  `ttcAggregateType`,
  `frequencyAggregateType`,
  `frequencyValueRepresentation`,
  `ttcValueRepresentation`,
  `sortField`,
  `sortOrder`,
  `dashboard`,
];

export const URL_QUERY_PARAMS = [
  `chartType`,
  `cohortFilters`,
  `conversionWindow`,
  `countType`,
  `dataGroupId`,
  `dateRange`,
  `comparedDateRange`,
  `eventFilters`,
  `events`,
  `exclusionSteps`,
  `funnelAttributionSelection`,
  `groupBy`,
  `holdingPropertyConstant`,
  `timeBuckets`,
  `frequencyBuckets`,
  `trendUnit`,
  `trendCountType`,
  `userFilters`,
  `funnelOrder`,
  `selectedTableColumns`,
];

const BOOKMARK_PARAM_TO_URL_TRANSLATION = {
  selectedSegments: `selected`,
};

const URL_PARAMS = {
  chartType: {
    decode: ({chartType}) => {
      const chartTypeDecoded = fromEnum(chartType, ChartType, null);
      return chartTypeDecoded === null ? {} : {chartType: chartTypeDecoded};
    },
    encode: passThrough(`chartType`, `query`),
  },
  cohortFilter: {
    decode: ({cohortFilter}) => {
      if (isEmpty(cohortFilter)) {
        return null;
      }

      // TODO: deprecate this param when there are no more logged instances
      reporter.info(`cohortFilter param decoded.`, {cohortFilter});

      return {
        cohortFilters: [
          {
            filterOperator: cohortFilter.negated ? ListOperator.DoesNotContain : ListOperator.Contains,
            filterValue: [cohortFilter],
            propertyType: PropertyType.List,
            resourceType: ResourceType.Cohort,
          },
        ],
      };
    },
  },
  cohortFilters: {
    decode: ({cohortFilters}) => {
      if (isEmpty(cohortFilters)) {
        return null;
      }

      // TODO: deprecate this `in` operator conversion
      if (cohortFilters.some((filter) => CohortOperatorListMap[filter.filterOperator])) {
        reporter.info(`cohortFilter deprecated operator decoded.`, {cohortFilters});
      }

      return {
        cohortFilters: cohortFilters.map((filter) => ({
          ...filter,
          filterOperator: CohortOperatorListMap[filter.filterOperator] || filter.filterOperator,
          propertyType: PropertyType.List,
        })),
      };
    },
    encode: passThrough(`cohortFilters`, `query`),
  },
  selected: {
    decode: passThrough(`selected`),
    encode: passThrough(`selected`),
  },
  selectMode: {
    decode: passThrough(`selectMode`),
    encode: passThrough(`selectMode`),
  },
  trendType: {
    decode: passThrough(`trendType`),
    encode: passThrough(`trendType`),
  },
  conversionWindow: {
    decode: passThrough(`conversionWindow`),
    encode: passThrough(`conversionWindow`, `query`),
  },
  countType: {
    decode: passThrough(`countType`),
    encode: passThrough(`countType`, `query`),
  },
  dataGroupId: {
    decode: passThrough(`dataGroupId`),
    encode: passThrough(`dataGroupId`, `query`),
  },
  dateRange: {
    decode: passThrough(`dateRange`),
    encode: ({query: {dateRange}}) => dateRange && {dateRange: dateRange.getAttributes()},
  },
  comparedDateRange: {
    decode: passThrough(`comparedDateRange`),
    encode: passThrough(`comparedDateRange`, `query`),
  },
  eventFilters: {
    decode: passThrough(`eventFilters`),
    encode: passThrough(`eventFilters`, `query`),
  },
  eventIndex: {
    decode: passThrough(`eventIndex`),
    encode: passThrough(`eventIndex`),
  },
  events: {
    decode: passThrough(`events`),
    encode: passThrough(`events`, `query`),
  },
  exclusionSteps: {
    decode: passThrough(`exclusionSteps`),
    encode: passThrough(`exclusionSteps`, `query`),
  },
  frequencyAggregateType: {
    decode: passThrough(`frequencyAggregateType`),
    encode: passThrough(`frequencyAggregateType`),
  },
  ttcAggregateType: {
    decode: passThrough(`ttcAggregateType`),
    encode: passThrough(`ttcAggregateType`),
  },
  frequencyType: {
    decode: passThrough(`frequencyType`),
    encode: passThrough(`frequencyType`),
  },
  frequencyVizType: {
    decode: passThrough(`frequencyVizType`),
    encode: passThrough(`frequencyVizType`),
  },
  ttcVizType: {
    decode: passThrough(`ttcVizType`),
    encode: passThrough(`ttcVizType`),
  },
  frequencyValueRepresentation: {
    decode: passThrough(`frequencyValueRepresentation`),
    encode: passThrough(`frequencyValueRepresentation`),
  },
  ttcValueRepresentation: {
    decode: passThrough(`ttcValueRepresentation`),
    encode: passThrough(`ttcValueRepresentation`),
  },
  groupBy: {
    decode: passThrough(`groupBy`),
    encode: passThrough(`groupBy`, `query`),
  },
  groupByOverride: {
    decode: ({groupByOverride}) => (groupByOverride ? {groupBy: groupByOverride} : {}),
  },
  erfTreatments: {
    decode: passThrough(`erfTreatments`), // to be merged with group bys in params or bookmark
  },
  holdingPropertyConstant: {
    decode: ({holdingPropertyConstant}) => {
      if (isEmpty(holdingPropertyConstant)) {
        return null;
      }

      if (holdingPropertyConstant.some((item) => item.value)) {
        // handle older urls
        return {
          holdingPropertyConstant: holdingPropertyConstant.map((item) => ({
            resourceType: ResourceType.Event,
            propertyType: PropertyType.String,
            propertyDefaultType: PropertyType.String,
            propertyName: item.value,
          })),
        };
      } else {
        return passThrough(`holdingPropertyConstant`)({holdingPropertyConstant});
      }
    },
    encode: passThrough(`holdingPropertyConstant`, `query`),
  },
  emailSource: {
    decode: ({emailSource}) =>
      emailSource && {
        odaParams: {
          task_id: emailSource.taskId,
          inbound_segment: emailSource.inboundSegment,
          oda_type: emailSource.odaType,
          oda_email_origin: true,
        },
      },
  },
  startStep: {
    decode: passThrough(`startStep`),
    encode: passThrough(`startStep`),
  },
  timeBuckets: {
    decode: passThrough(`timeBuckets`),
    encode: passThrough(`timeBuckets`, `query`),
  },
  frequencyBuckets: {
    decode: passThrough(`frequencyBuckets`),
    encode: passThrough(`frequencyBuckets`, `query`),
  },
  trendUnit: {
    decode: ({trendUnit}) => Object.values(TrendUnit).includes(trendUnit) && {trendUnit},
    encode: passThrough(`trendUnit`, `query`),
  },
  trendCountType: {
    decode: ({trendCountType}) => Object.values(TrendCountType).includes(trendCountType) && {trendCountType},
    encode: passThrough(`trendCountType`, `query`),
  },
  userFilters: {
    decode: passThrough(`userFilters`),
    encode: passThrough(`userFilters`, `query`),
  },
  incomingUrlVersion: {
    decode: ({incomingUrlVersion}) => ({urlVersion: incomingUrlVersion}),
  },
  funnelOrder: {
    decode: passThrough(`funnelOrder`),
    encode: passThrough(`funnelOrder`, `query`),
  },
  querySampling: {
    decode: passThrough(`querySampling`),
    encode: passThrough(`querySampling`),
  },
  funnelAttributionSelection: {
    decode: passThrough(`funnelAttributionSelection`),
    encode: passThrough(`funnelAttributionSelection`, `query`),
  },
  additionalFilters: {
    decode: passThrough(`additionalFilters`), // to be merged with global filters in params or bookmark
  },
  chartTypeOverride: {
    decode: passThrough(`chartTypeOverride`),
  },
  dateRangeOverride: {
    decode: passThrough(`dateRangeOverride`),
  },
  sortField: {
    decode: passThrough(`sortField`),
    encode: passThrough(`sortField`),
  },
  sortOrder: {
    decode: passThrough(`sortOrder`),
    encode: passThrough(`sortOrder`),
  },
  selectedTableColumns: {
    decode: passThrough(`selectedTableColumns`),
    encode: passThrough(`selectedTableColumns`, `query`),
  },
  dashboard: {
    decode: ({dashboard}) => {
      return dashboard ? {dashboard} : {};
    },
    encode: ({dashboard}) => {
      return dashboard ? {dashboard} : {};
    },
  },
};

function v1ToV2(v1Params) {
  let v2Params = {};
  if (`conversion_unit` in v1Params) {
    v2Params[`trendUnit`] = v1Params[`conversion_unit`];
  }
  if (`event_index` in v1Params) {
    v2Params[`eventIndex`] = v1Params[`event_index`];
  }
  if (`start_step` in v1Params) {
    v2Params[`startStep`] = v1Params[`start_step`];
  }

  if (`segfilter` in v1Params) {
    v2Params = {...v2Params, ...paramsFromSegfilter(v1Params[`segfilter`])};
  } else if (`group_by_selected_cohorts` in v1Params) {
    v2Params = {...v2Params, ...paramsFromGroupBySelectedCohorts(v1Params[`group_by_selected_cohorts`])};
  }

  if (`from_date` in v1Params && `to_date` in v1Params) {
    v2Params[`dateRange`] = relativeDatesToFullLengthDateRange({
      relativeFrom: v1Params.from_date,
      relativeTo: v1Params.to_date,
    });
  }
  if (`email_source` in v1Params) {
    v2Params[`emailSource`] = {
      taskId: v1Params[`email_source`].task_id,
      inboundSegment: v1Params[`email_source`].inbound_segment,
      odaType: v1Params[`email_source`].oda_type,
    };
  }
  v2Params[`v`] = 2;
  return v2Params;
}

function updateParams(oldParams) {
  let newParams = {};
  if (!(`v` in oldParams) || oldParams[`v`] === 1) {
    newParams = updateParams(v1ToV2(oldParams));
  } else if (oldParams[`v`] === 2) {
    newParams = oldParams;
  } else {
    reporter.error(new Error(`Unrecognized URL version`), oldParams[`v`]);
  }
  newParams[`incomingUrlVersion`] = oldParams[`v`] || 0;
  return newParams;
}

export function decode(paramString) {
  let urlData = {};
  const params = rison.parse(`(${paramString || ``})`);
  if (!params) {
    return urlData;
  }

  const overrides: BookmarkOverrides = pick(params, bookmarkOverrides);
  const latestParams = {
    ...updateParams(params),
    ...overrides,
  };
  Object.keys(URL_PARAMS).forEach((param) => {
    const decoder = URL_PARAMS[param].decode;
    if (decoder) {
      urlData = {...urlData, ...decoder(latestParams)};
    }
  });
  return urlData;
}

export function encode(appState, {version}) {
  let urlParams = {};
  Object.keys(URL_PARAMS).forEach((param) => {
    const encoder = URL_PARAMS[param].encode;
    if (encoder) {
      urlParams = {...urlParams, ...encoder(appState)};
    }
  });

  urlParams[`v`] = version;

  const encoded = rison.urlencode(urlParams);
  return encoded.substring(1, encoded.length - 1);
}

export function translateBookmarkParamsToUrlParams(bookmark: FunnelBookmark) {
  // Necessary for some funnel params which are encoded as a different keyword in the url than in the bookmark
  // This is temporary code which will be replaced soon with a non-url based solution - Bruna Feb 9th 2020
  const urlParams = {};
  const paramsToTranslate = pick(bookmark, Object.keys(BOOKMARK_PARAM_TO_URL_TRANSLATION));
  Object.keys(paramsToTranslate).forEach((param) => {
    const translateTo = BOOKMARK_PARAM_TO_URL_TRANSLATION[param];
    urlParams[translateTo] = paramsToTranslate[param];
  });
  return urlParams;
}

// after urlData is processed by decode, overrides could be changed into other names like the below. Filter these
// additional ones out if they show up since they may be overrides from dashboards. If we aren't explicitly overridden
// by dashboards but the bookmark has still changed, we'd get a bunch more of these so it's ok for omit
const processedBookmarkOverrides = [
  ...bookmarkOverrides,
  `chartType`,
  `dateRange`,
  `emailSource`,
  `groupBy`,
  `odaParams`,
  `trendUnit`,
  `urlVersion`,
  `dashboard`,
];

export function bookmarkHasUnsavedChanges(urlData) {
  return Object.keys(omit(urlData, processedBookmarkOverrides)).length > 0;
}

export function bookmarkHasDashboardOverrides(urlData) {
  return Object.keys(pick(urlData, processedBookmarkOverrides)).length > 0;
}
